import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  SmartConnectorContext,
  SmartConnectorErrors,
} from '../SmartConnectorWizard/context';
import { useFlashTransition } from 'hooks/useFlashState';
import { ExecutionVariable, SmartConnector } from 'pages/SmartConnectors/types';

const errorsOrder: Partial<Record<keyof SmartConnector, number>> = {
  name: 0,
  custom_object: 1,
  execution_variables: 2,
};

const variableErrorsOrder: Partial<Record<keyof ExecutionVariable, number>> = {
  name: 0,
  required: 1,
  data_source: 2,
  data_type: 3,
  input_format: 4,
  is_array: 5,
  array_delimiter: 6,
  output_format: 7,
  display_order: 8,
};

export const useErrors = (errorKey: keyof SmartConnectorErrors) => {
  const { errors, setErrors } = useContext(SmartConnectorContext);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const error = errors[errorKey];

  const firstErrorKey = (Object.keys(errors) as (keyof SmartConnector)[]).sort(
    (a, b) => errorsOrder[a]! - errorsOrder[b]!
  )[0];

  const isFirstError = error && firstErrorKey === errorKey;

  useEffect(() => {
    if (isFirstError) {
      flashErrorMessage(error);
    }
  }, [error, isFirstError, flashErrorMessage]);

  const clearError = useCallback(() => {
    if (showMessage) {
      cancelFlash();
    }

    if (error) {
      setErrors((prev) => {
        const { [errorKey]: _, ...rest } = prev;
        return rest;
      });
    }
  }, [showMessage, cancelFlash, error, setErrors, errorKey]);

  const validate = useMemo(
    () => ({ showMessage, message }),
    [showMessage, message]
  );

  return {
    error: message || error,
    validate,
    clearError,
  };
};

export const useVariableErrors = (
  index: number,
  scope: string,
  setScope: Dispatch<SetStateAction<string>>
) => {
  const {
    smartConnector,
    errors: { execution_variables = [] },
    setErrors,
  } = useContext(SmartConnectorContext);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const scopeExists = !!smartConnector.headers?.[scope];

  const [variableError, hasError, errorKey, errorMessage] = useMemo(() => {
    const variableError = (execution_variables || [])[index] || null;

    const hasError = Boolean(
      scopeExists && variableError && Object.values(variableError).length
    );

    const isFirstVariableError =
      hasError &&
      !(execution_variables || []).some(
        (el, i) =>
          i < index && el && typeof el === 'object' && Object.values(el).length
      );
    const errorKey = isFirstVariableError
      ? (Object.keys(variableError) as (keyof ExecutionVariable)[]).sort(
          (a, b) => variableErrorsOrder[a]! - variableErrorsOrder[b]!
        )[0]!
      : null;
    return [
      variableError,
      hasError,
      errorKey,
      errorKey ? variableError[errorKey] : null,
    ];
  }, [execution_variables, index, scopeExists]);

  useEffect(() => {
    if (errorMessage) {
      setScope(scope);
      flashErrorMessage(errorMessage);
    }
  }, [flashErrorMessage, errorMessage, setScope, scope]);

  const clearError = useCallback(
    (prop: keyof ExecutionVariable) => {
      if (hasError) {
        if (showMessage && errorKey === prop) {
          cancelFlash();
        }

        setErrors((prev) => ({
          ...prev,
          execution_variables: (prev.execution_variables || []).map((el, i) => {
            if (i === index) {
              const { [prop]: _, ...rest } = el;
              return rest;
            }
            return el;
          }),
        }));
      }
    },
    [showMessage, cancelFlash, hasError, setErrors, errorKey, index]
  );

  const validate = useMemo(
    () => ({ showMessage, message }),
    [showMessage, message]
  );

  return {
    hasError,
    variableError,
    errorKey,
    validate,
    clearError,
  };
};
